/**
 * MedicalChatbot Component
 * ========================
 * LLM-powered Q&A interface for patient medical history
 *
 * Features:
 * - Natural language query interface
 * - Suggested questions with one-click query
 * - Conversation history display
 * - Source attribution and confidence indicators
 *
 * Based on: CONVERSATIONAL_NOTES_ENHANCEMENTS_PLAN.md
 *
 * Author: Claude Code
 * Date: 2026-01-14
 */

import React, { useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { useMedicalChatbot } from '../../hooks/useMedicalChatbot';

// ==========================================
// TYPES
// ==========================================

interface MedicalChatbotProps {
  patientId?: string;
  sessionId?: string;
  autoLoadSuggestions?: boolean;
}

// ==========================================
// COMPONENT
// ==========================================

export const MedicalChatbot: React.FC<MedicalChatbotProps> = ({
  patientId,
  sessionId,
  autoLoadSuggestions = true,
}) => {
  const {
    messages,
    query,
    isQuerying,
    queryError,
    suggestedQuestions,
    loadSuggestedQuestions,
    suggestionsLoading,
    suggestionsError,
    currentQuestion,
    setCurrentQuestion,
    clearMessages,
  } = useMedicalChatbot({
    patientId,
    sessionId,
    autoLoadSuggestions,
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  /** When true, skip next scroll (e.g. after clicking a quick prompt) so we don't jump to bottom. */
  const skipNextScrollRef = useRef(false);

  useEffect(() => {
    if (skipNextScrollRef.current) {
      skipNextScrollRef.current = false;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ==========================================
  // HANDLERS
  // ==========================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentQuestion.trim() && !isQuerying) {
      await query(currentQuestion);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    skipNextScrollRef.current = true;
    setCurrentQuestion(suggestion);
    query(suggestion);
  };

  const getConfidenceColor = (confidence: string): 'success' | 'warning' | 'error' => {
    switch (confidence) {
      case 'high':
        return 'success';
      case 'medium':
        return 'warning';
      default:
        return 'error';
    }
  };

  // ==========================================
  // RENDER HELPERS – Ask Sensei design tokens
  // ==========================================
  const SENSEI_DARK_BLUE = '#012676';
  const SENSEI_MEDIUM_BLUE = '#2563eb';
  const SENSEI_BORDER_BLUE = '#93c5fd';
  const SENSEI_LIGHT_BG = '#E8F4FD';

  /**
   * Render quick prompts section – design: stacked vertically, left-aligned;
   * bold dark blue label; first two prompts white bg, third very light blue; thin blue border; medium blue text.
   */
  const renderSuggestedQuestions = () => {
    if (!patientId) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          No patient selected. Please select a patient to see quick prompts.
        </Alert>
      );
    }

    if (suggestionsLoading) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" py={2}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Loading quick prompts...
          </Typography>
        </Box>
      );
    }

    if (suggestionsError) {
      return (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <IconButton size="small" onClick={loadSuggestedQuestions}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        >
          {suggestionsError}
        </Alert>
      );
    }

    if (suggestedQuestions.length === 0) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          No quick prompts available. Try asking a question about the patient.
        </Alert>
      );
    }

    return (
      <Box sx={{ mb: 1.25 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" fontWeight={700} sx={{ color: SENSEI_DARK_BLUE, fontSize: '0.75rem' }}>
            Quick prompts:
          </Typography>
          <Tooltip title="Refresh prompts">
            <IconButton size="small" onClick={loadSuggestedQuestions} sx={{ color: SENSEI_DARK_BLUE }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75, mb: 0.5 }}>
          {suggestedQuestions.slice(0, 4).map((suggestion, index) => (
            <Box
              key={index}
              component="button"
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              disabled={isQuerying}
              sx={{
                width: '100%',
                textAlign: 'left',
                px: 1.25,
                py: 0.65,
                borderRadius: 1.25,
                border: `1px solid ${SENSEI_BORDER_BLUE}`,
                bgcolor: index === 2 ? SENSEI_LIGHT_BG : '#FFFFFF',
                color: SENSEI_MEDIUM_BLUE,
                fontSize: '0.75rem',
                fontWeight: 500,
                cursor: isQuerying ? 'default' : 'pointer',
                '&:hover': isQuerying ? {} : { bgcolor: index === 2 ? '#D6EEFC' : '#F5F9FF', borderColor: SENSEI_MEDIUM_BLUE },
              }}
            >
              {suggestion}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  /**
   * Render conversation messages
   */
  const renderMessages = () => {
    if (messages.length === 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={4}
          sx={{ opacity: 0.6 }}
        >
          <QuestionAnswerIcon sx={{ fontSize: 60, mb: 2, color: 'text.secondary' }} />
          <Typography variant="body1" color="text.secondary" align="center">
            Ask questions about the patient's medical history
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            Click a suggested question above or type your own
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ maxHeight: '400px', overflowY: 'auto', mb: 2 }}>
        {messages.map((message, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            {/* Question – right-aligned, white bubble, thin blue border (design) */}
            <Box display="flex" alignItems="flex-start" justifyContent="flex-end" mb={1}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: '#FFFFFF',
                  color: 'text.primary',
                  borderRadius: 2,
                  border: '1px solid #93c5fd',
                  maxWidth: '80%',
                }}
              >
                <Typography variant="body2">{message.question}</Typography>
              </Paper>
              <PersonIcon sx={{ ml: 1, mt: 0.5, color: '#012676' }} fontSize="small" />
            </Box>

            {/* Answer – left-aligned, light blue bubble, thin blue border (design) */}
            <Box display="flex" alignItems="flex-start" mb={1}>
              <SmartToyIcon sx={{ mr: 1, mt: 0.5, color: '#012676' }} fontSize="small" />
              <Box sx={{ maxWidth: '80%' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: '#E8F4FD',
                    borderRadius: 2,
                    border: '1px solid #93c5fd',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                  >
                    {message.answer}
                  </Typography>
                </Paper>

                {/* Metadata */}
                <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                  {/* Confidence indicator */}
                  <Chip
                    label={`${message.confidence} confidence`}
                    size="small"
                    color={getConfidenceColor(message.confidence)}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />

                  {/* Sources */}
                  {message.sources.map((source, i) => (
                    <Chip
                      key={i}
                      label={source}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  ))}

                  {/* Timestamp */}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, mt: 0.2 }}>
                    {new Date(message.generated_at).toLocaleTimeString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (currentQuestion.trim() && !isQuerying) {
        query(currentQuestion);
      }
    }
  };

  /**
   * Render input form – layout and styling per design (light border, send button with paper airplane)
   */
  const renderInputForm = () => {
    if (!patientId) {
      return (
        <Alert severity="info">
          Select a patient to start asking questions.
        </Alert>
      );
    }

    return (
      <Box component="form" onSubmit={handleSubmit}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 0,
            border: '1px solid #93c5fd',
            borderRadius: 1.5,
            bgcolor: '#F5F5F5',
            overflow: 'hidden',
          }}
        >
          <TextField
            fullWidth
            variant="standard"
            placeholder="Ask a question about this patient..."
            value={currentQuestion}
            onChange={(e) => setCurrentQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isQuerying}
            multiline
            maxRows={4}
            InputProps={{
              disableUnderline: true,
              sx: { px: 1.5, py: 1.25, fontSize: '0.9375rem' },
            }}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <IconButton
            type="submit"
            disabled={isQuerying || !currentQuestion.trim()}
            sx={{
              bgcolor: '#012676',
              color: '#fff',
              borderRadius: 0,
              width: 48,
              height: 48,
              '&:hover': { bgcolor: '#03408a' },
              '&.Mui-disabled': { bgcolor: '#E0E0E0', color: '#9E9E9E' },
            }}
          >
            {isQuerying ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>

        {queryError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {queryError}
          </Alert>
        )}
      </Box>
    );
  };

  // ==========================================
  // MAIN RENDER
  // ==========================================

  return (
    <Box sx={{ width: '100%' }}>
      <Card variant="outlined">
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            {/* <Box display="flex" alignItems="center" gap={1}>
              <QuestionAnswerIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Medical Chatbot
              </Typography>
            </Box> */}

            {messages.length > 0 && (
              <Tooltip title="Clear conversation">
                <IconButton size="small" onClick={clearMessages}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
            Get instant AI-powered clinical guidance and recommendations
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {/* Quick prompts */}
          {renderSuggestedQuestions()}

          <Divider sx={{ mb: 2 }} />

          {/* Conversation */}
          {renderMessages()}

          {/* Input Form */}
          {renderInputForm()}

          {/* Help Text */}
          {/* {patientId && messages.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Example questions: "What was the patient's last HbA1c?", "Is the patient on insulin?",
              "When was the patient last seen?", "Does the patient have any medication allergies?"
            </Typography>
          )} */}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MedicalChatbot;
